// @flow
import 'isomorphic-fetch'
import { API, createDefaultHeaders } from '../../util/api'
import { isInvalid } from '@rachio-npm/com-rachio-web-core/util/validation'
import { isShareCode } from '../../util/location'
import { logger } from '../../util/logger'
import * as actionCreators from './index'
import * as scheduleSchema from './schemas'
import changeCase from 'change-object-case'
import moment from 'moment'
import omit from 'lodash.omit'

// Types
import type { Dispatch } from '../../util/redux'

/**
 * Additional methods around schedule functionality
 * @module ducks/schedule/methods
 */

/**
 * Run Schedules
 * @param {string} authToken - User Auth Token
 * @param {string} scheduleId - Schedule ID
 * @returns {promise} - Api call
 */
export const run = (authToken: string, scheduleId: string) => {
  return function() {
    const Api = new API()
    const reqData = changeCase.snakeKeys({ scheduleId })
    const headers = {
      ...createDefaultHeaders(authToken),
      'Content-Type': 'application/json'
    }

    // Return API call
    return Api.call('schedule/runSchedule', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(reqData)
    }).then(response => response.json())
  }
}

type ZoneRun = {|
  duration: number,
  zoneNumber: number
|}

/**
 * Get `customRun` config
 * @param  {string} authToken - Auth token OR share code
 * @param  {string} deviceId - Device ID
 * @param  {object[]} zones - Zone runs
 * @return {object} - Config object
 */
const customRunModes = (
  authToken: string,
  deviceId: string,
  zones: Array<ZoneRun>
) => {
  const reqObject = { deviceId, runs: zones }
  const props = {
    limitedAccess: {
      headers: {
        ...createDefaultHeaders(),
        'Content-Type': 'application/json'
      },
      url: 'device/setManualScheduleQR',
      reqData: changeCase.snakeKeys(
        { ...reqObject, code: authToken },
        { recursive: true, arrayRecursive: true }
      )
    },
    fullAccess: {
      headers: {
        ...createDefaultHeaders(authToken),
        'Content-Type': 'application/json'
      },
      url: 'device/setManualSchedule',
      reqData: changeCase.snakeKeys(reqObject, {
        recursive: true,
        arrayRecursive: true
      })
    }
  }

  if (isShareCode(authToken)) {
    return props.limitedAccess
  } else {
    return props.fullAccess
  }
}

/**
 * Run Custom Schedule
 * @param {string} authToken - User Auth Token
 * @param {string} deviceId - Device ID
 * @param {object[]} zones - Zone Objects
 * @param {number} zones[].zoneNumber - Zone number (for device)
 * @param {number} zones[].duration - The duration of the zone
 * @returns {promise} - Api call
 */
export const customRun = (
  authToken: string,
  deviceId: string,
  zones: Array<ZoneRun>
) => {
  return function() {
    const Api = new API()
    const mode = customRunModes(authToken, deviceId, zones)

    // Return API call
    return Api.call(mode.url, {
      method: 'POST',
      headers: mode.headers,
      body: JSON.stringify(mode.reqData)
    }).then(response => response.json())
  }
}

/**
 * Get `stop` config
 * @param  {string} authToken - Auth token OR share code
 * @param  {string} deviceId - Device ID
 * @return {object} - Config object
 */
const stopModes = (authToken: string, deviceId: string) => {
  const reqObj = { deviceId }
  const props = {
    limitedAccess: {
      headers: {
        ...createDefaultHeaders(),
        'Content-Type': 'application/json'
      },
      url: 'device/stopWateringQR',
      reqData: changeCase.snakeKeys(
        { ...reqObj, code: authToken },
        { recursive: true, arrayRecursive: true }
      ),
      reqObj
    },
    fullAccess: {
      headers: {
        ...createDefaultHeaders(authToken),
        'Content-Type': 'application/json'
      },
      url: 'device/stopWatering',
      reqData: changeCase.snakeKeys(reqObj, {
        recursive: true,
        arrayRecursive: true
      }),
      reqObj
    }
  }

  if (isShareCode(authToken)) {
    return props.limitedAccess
  } else {
    return props.fullAccess
  }
}

/**
 * Stop Schedule
 * @param {string} authToken - User Auth Token
 * @param {string} deviceId - Device ID
 * @returns {promise} - Api call
 */
export const stop = (authToken: string, deviceId: string) => {
  return function(): Promise<*> {
    const Api = new API()
    const mode = stopModes(authToken, deviceId)
    const error = isInvalid(
      scheduleSchema.StopWatering,
      omit(mode.reqData, ['code'])
    )
    const headers = mode.headers

    if (error) {
      logger.error(error)
      return Promise.reject({ error: true, payload: error })
    }

    // Return API call
    return Api.call(mode.url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(mode.reqData)
    }).then(response => response.json())
  }
}

/**
 * Get `pause` config
 * @param  {string} authToken - Auth token OR share code
 * @param  {string} deviceId - Device ID
 * @param  {number} secondsPaused - Number of seconds to pause watering
 * @return {object} - Config object
 */
const pauseModes = (
  authToken: string,
  deviceId: string,
  secondsPaused: number
) => {
  const reqObject = { deviceId, secondsPaused }
  const props = {
    limitedAccess: {
      headers: {
        ...createDefaultHeaders(),
        'Content-Type': 'application/json'
      },
      url: 'device/pauseZoneRunQR',
      reqData: changeCase.snakeKeys(
        { ...reqObject, code: authToken },
        { recursive: true, arrayRecursive: true }
      ),
      reqObject
    },
    fullAccess: {
      headers: {
        ...createDefaultHeaders(authToken),
        'Content-Type': 'application/json'
      },
      url: 'device/pauseZoneRun',
      reqData: changeCase.snakeKeys(reqObject, {
        recursive: true,
        arrayRecursive: true
      }),
      reqObject
    }
  }

  if (isShareCode(authToken)) {
    return props.limitedAccess
  } else {
    return props.fullAccess
  }
}

/**
 * Pause Schedule
 * @param {string} authToken - User Auth Token
 * @param {string} deviceId - Device ID
 * @param {number} secondsPaused - How many seconds to pause
 * @returns {promise} - Api call
 */
export const pause = (
  authToken: string,
  deviceId: string,
  secondsPaused: number
) => {
  return function(): Promise<*> {
    const Api = new API()
    const mode = pauseModes(authToken, deviceId, secondsPaused)
    const error = isInvalid(
      scheduleSchema.PauseZoneRunRequestSchema,
      omit(mode.reqData, ['code'])
    )
    const headers = mode.headers

    if (error) {
      logger.error(error)
      return Promise.reject({ error: true, payload: error })
    }

    // Return API call
    return Api.call(mode.url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(mode.reqData)
    }).then(response => response.json())
  }
}

/**
 * Get `nextZone` config
 * @param  {string} authToken - Auth token OR share code
 * @param  {string} deviceId - Device ID
 * @return {object} - Config object
 */
const nextZoneModes = (authToken: string, deviceId: string) => {
  const reqObject = { deviceId }
  const props = {
    limitedAccess: {
      headers: {
        ...createDefaultHeaders(),
        'Content-Type': 'application/json'
      },
      url: 'device/skipForwardZoneRunQR',
      reqData: changeCase.snakeKeys(
        { ...reqObject, code: authToken },
        { recursive: true, arrayRecursive: true }
      ),
      reqObject
    },
    fullAccess: {
      headers: {
        ...createDefaultHeaders(authToken),
        'Content-Type': 'application/json'
      },
      url: 'device/skipForwardZoneRun',
      reqData: changeCase.snakeKeys(reqObject, {
        recursive: true,
        arrayRecursive: true
      }),
      reqObject
    }
  }

  if (isShareCode(authToken)) {
    return props.limitedAccess
  } else {
    return props.fullAccess
  }
}

/**
 * Go to the next zone in the schedule
 * @param {string} authToken - User Auth Token
 * @param {string} deviceId - Device ID
 * @returns {promise} - Api call
 */
export const nextZone = (authToken: string, deviceId: string) => {
  return function(): Promise<*> {
    const Api = new API()
    const mode = nextZoneModes(authToken, deviceId)
    const error = isInvalid(
      scheduleSchema.SkipForwardZoneRunRequestSchema,
      omit(mode.reqData, ['code'])
    )
    const headers = mode.headers

    if (error) {
      logger.error(error)
      return Promise.reject({ error: true, payload: error })
    }

    // Return API call
    return Api.call(mode.url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(mode.reqData)
    }).then(response => response.json())
  }
}

/**
 * Get `previousZone` config
 * @param  {string} authToken - Auth token OR share code
 * @param  {string} deviceId - Device ID
 * @return {object} - Config object
 */
const previousZoneModes = (authToken: string, deviceId: string) => {
  const reqObject = { deviceId }
  const props = {
    limitedAccess: {
      headers: {
        ...createDefaultHeaders(),
        'Content-Type': 'application/json'
      },
      url: 'device/skipBackwardZoneRunQR',
      reqData: changeCase.snakeKeys(
        { ...reqObject, code: authToken },
        { recursive: true, arrayRecursive: true }
      ),
      reqObject
    },
    fullAccess: {
      headers: {
        ...createDefaultHeaders(authToken),
        'Content-Type': 'application/json'
      },
      url: 'device/skipBackwardZoneRun',
      reqData: changeCase.snakeKeys(reqObject, {
        recursive: true,
        arrayRecursive: true
      }),
      reqObject
    }
  }

  if (isShareCode(authToken)) {
    return props.limitedAccess
  } else {
    return props.fullAccess
  }
}

/**
 * Go to the previous zone in the schedule
 * @param {string} authToken - User Auth Token
 * @param {string} deviceId - Device ID
 * @returns {promise} - Api call
 */
export const previousZone = (authToken: string, deviceId: string) => {
  return function(): Promise<*> {
    const Api = new API()
    const mode = previousZoneModes(authToken, deviceId)
    const error = isInvalid(
      scheduleSchema.SkipBackwardZoneRunRequestSchema,
      omit(mode.reqData, ['code'])
    )
    const headers = {
      ...createDefaultHeaders(authToken),
      'Content-Type': 'application/json'
    }

    if (error) {
      logger.error(error)
      return Promise.reject({ error: true, payload: error })
    }

    // Return API call
    return Api.call(mode.url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(mode.reqData)
    }).then(response => response.json())
  }
}

/**
 * Copy schedule
 * @param {string} userAuthToken - User's authorization token
 * @param {string} scheduleId - Schedule ID
 * @returns {object} An action related to the schedule copy process
 */
export const copy = (userAuthToken: string, scheduleId: string) => {
  return function(dispatch: Dispatch): Promise<*> {
    const Api = new API()
    const reqData = changeCase.snakeKeys({ scheduleId }, { recursive: true })
    const error = isInvalid(scheduleSchema.CopyScheduleRequestSchema, reqData)
    const reqObj = {
      method: 'POST',
      body: JSON.stringify(reqData),
      headers: new Headers({
        'Content-Type': 'application/json',
        ...createDefaultHeaders(userAuthToken)
      })
    }

    dispatch(actionCreators.scheduleCopyInit(reqData))

    if (error) {
      return Promise.reject(
        dispatch(actionCreators.scheduleCopyComplete(error))
      )
    }

    return Api.call('schedule/copySchedule', reqObj)
      .then(response =>
        response
          .json()
          .then(json => dispatch(actionCreators.scheduleCopyComplete(json)))
      )
      .catch(err => dispatch(actionCreators.scheduleCopyComplete(err)))
  }
}

/**
 * Get runs for a schedule
 * @param  {string} userAuthToken - User auth token
 * @param  {string} scheduleId - Schedule ID
 * @param  {string} startTimeInSeconds - Start time in seconds; ISO8601 standard (eg: 2013-02-04T22:44:30.652Z)
 * @param  {string} endTimeInSeconds - End time in seconds; ISO8601 standard (eg: 2013-02-04T22:44:30.652Z)
 * @returns {object} An action related to getting schedule runs
 */
export const getCalendar = (
  userAuthToken: string,
  scheduleId: string,
  startTimeInSeconds: string = moment().valueOf(),
  endTimeInSeconds: string = moment()
    .add(2, 'weeks')
    .valueOf()
) => {
  return function(dispatch: Dispatch) {
    const Api = new API()
    const startTime = moment(startTimeInSeconds).toISOString()
    const endTime = moment(endTimeInSeconds).toISOString()
    const reqData = changeCase.snakeKeys(
      { scheduleId, startTime: startTime, endTime: endTime },
      { recursive: true }
    )
    const reqObj = {
      method: 'POST',
      body: JSON.stringify(reqData),
      headers: new Headers({
        'Content-Type': 'application/json',
        ...createDefaultHeaders(userAuthToken)
      })
    }

    dispatch(actionCreators.calendarGetInit(reqData))

    return Api.call('schedule/getScheduleRuns', reqObj)
      .then(response => response.json())
      .then(json =>
        dispatch(actionCreators.calendarGetComplete({ ...json, scheduleId }))
      )
      .catch(err => dispatch(actionCreators.calendarGetComplete(err)))
  }
}

export const getPreview = (authToken: string, scheduleData: Object) => {
  return function(dispatch: Dispatch): Promise<*> {
    const reqData = changeCase.snakeKeys(scheduleData, {
      recursive: true,
      arrayRecursive: true
    })
    const Api = new API()
    const error = isInvalid(
      scheduleSchema.SchedulePreviewRequestSchema,
      reqData
    )
    const reqObj = {
      method: 'POST',
      body: JSON.stringify(reqData),
      headers: {
        ...createDefaultHeaders(authToken),
        'Content-Type': 'application/json'
      }
    }

    if (error) {
      logger.error(error)
      return Promise.reject(
        dispatch(actionCreators.schedulePreviewComplete(error))
      )
    }

    dispatch(actionCreators.schedulePreviewInit(scheduleData))

    return Api.call('schedule/previewSchedule', reqObj)
      .then(res => res.json())
      .then(json => dispatch(actionCreators.schedulePreviewComplete(json)))
      .catch(err => {
        logger.error(err)
        dispatch(actionCreators.schedulePreviewComplete(err))
      })
  }
}

/**
 * Set/disable schedule run skips (e.g. future feed skip/run buttons)
 * @param {string} token user auth token
 * @param {string} action the activity object from the event
 * @returns {Promise} a promise that returns a new array of skips [proto: SkipSequence]
 */
export const patchActivityAction = (
  token: string,
  action: { entityId: string, type: string }
) => {
  return function(dispatch: Dispatch): Promise<*> {
    const Api = new API()
    const reqData = changeCase.snakeKeys(
      { action },
      { recursive: true, arrayRecursive: true }
    )

    const reqObj = {
      method: 'PUT',
      body: JSON.stringify(reqData),
      headers: {
        ...createDefaultHeaders(token),
        'Content-Type': 'application/json'
      }
    }

    dispatch(actionCreators.scheduleSetSkipInit(reqObj))

    return Api.call('events/patchActivityAction', reqObj)
      .then(res => res.json())
      .then(json => dispatch(actionCreators.scheduleSetSkipComplete(json)))
      .catch(err => {
        logger.error(err)
        dispatch(actionCreators.scheduleSetSkipComplete(err))
      })
  }
}
